/**
 * v1.8 AP-11 (P19) — minimaler `.xlsx`-Round-trip für Listen. Kein neuer
 * schwerer Abhängigkeits-Zug: `fflate` (Zip/Inflate) ist bereits im Build
 * (Excalidraw-Baum) und wird für den ZIP-Container genutzt — `.xlsx` ist eine
 * ZIP aus XML. Wir arbeiten bewusst auf der `string[][]`-Matrix-Ebene (dieselbe,
 * die der CSV-Import erzeugt): so teilen Excel- und CSV-Import denselben
 * „Header→Spalten, Datenzeilen→Rows"-Pfad, und der Export ist symmetrisch.
 *
 * Export: eine Tabelle mit `inlineStr`-Zellen (keine sharedStrings nötig).
 * Import: liest sowohl `inlineStr`/`str` als auch `sharedStrings` (das schreibt
 * echtes Excel) und ordnet über die A1-Zellreferenz spaltengenau ein (Lücken
 * werden als leere Zellen aufgefüllt).
 */

import { unzipSync, zipSync } from "fflate";

// Text ↔ Bytes über die Standard-Web-APIs statt fflates strToU8/strFromU8:
// letztere liefern in manchen Test-Realms ein Uint8Array aus einem fremden
// Realm, das interne instanceof-Checks nicht besteht. TextEncoder/-Decoder sind
// realm-neutral und überall verfügbar.
// Das erneute Umschließen in `new Uint8Array(...)` erzwingt den lokalen Realm —
// fflate prüft intern `instanceof Uint8Array`, und ein TextEncoder-Ergebnis aus
// einem fremden Realm würde diese Prüfung nicht bestehen (fflate deutete es dann
// als verschachteltes Verzeichnis-Objekt).
const enc = (s: string): Uint8Array =>
  new Uint8Array(new TextEncoder().encode(s));
const dec = (b: Uint8Array): string =>
  new TextDecoder().decode(new Uint8Array(b));

/** 0-basierter Spaltenindex → Excel-Spaltenbuchstabe (0→A, 25→Z, 26→AA). */
export const colLetter = (index: number): string => {
  let n = index;
  let s = "";
  do {
    s = String.fromCharCode(65 + (n % 26)) + s;
    n = Math.floor(n / 26) - 1;
  } while (n >= 0);
  return s;
};

/** Excel-Zellreferenz (z. B. "AB12") → 0-basierter Spaltenindex. */
export const colIndexOfRef = (ref: string): number => {
  const m = /^([A-Z]+)/.exec(ref);
  if (!m) {
    return 0;
  }
  let n = 0;
  for (const ch of m[1]) {
    n = n * 26 + (ch.charCodeAt(0) - 64);
  }
  return n - 1;
};

const xmlEscape = (s: string): string =>
  s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

/** Baut eine minimale `.xlsx`-Datei aus einer Zeilen-Matrix (Strings). */
export const matrixToXlsx = (matrix: string[][]): Uint8Array => {
  const rowsXml = matrix
    .map((row, r) => {
      const cells = row
        .map((val, c) => {
          const ref = `${colLetter(c)}${r + 1}`;
          return `<c r="${ref}" t="inlineStr"><is><t xml:space="preserve">${xmlEscape(
            String(val ?? ""),
          )}</t></is></c>`;
        })
        .join("");
      return `<row r="${r + 1}">${cells}</row>`;
    })
    .join("");

  const sheet = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><sheetData>${rowsXml}</sheetData></worksheet>`;

  const workbook = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"><sheets><sheet name="Liste" sheetId="1" r:id="rId1"/></sheets></workbook>`;

  const workbookRels = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/></Relationships>`;

  const rootRels = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/></Relationships>`;

  const contentTypes = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types"><Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/><Default Extension="xml" ContentType="application/xml"/><Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/><Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/></Types>`;

  return zipSync({
    "[Content_Types].xml": enc(contentTypes),
    "_rels/.rels": enc(rootRels),
    "xl/workbook.xml": enc(workbook),
    "xl/_rels/workbook.xml.rels": enc(workbookRels),
    "xl/worksheets/sheet1.xml": enc(sheet),
  });
};

// Namespace-agnostisch nach lokalem Namen suchen — die xlsx-XML nutzt eine
// Default-Namespace, für die getElementsByTagName(local) nicht zuverlässig
// greift; getElementsByTagNameNS("*", …) matcht unabhängig vom Namespace.
const els = (
  node: Document | Element,
  name: string,
): HTMLCollectionOf<Element> => node.getElementsByTagNameNS("*", name);

/** Alle <t>-Textknoten eines Elements konkateniert (für <si>/<is>). */
const textOf = (el: Element | null): string => {
  if (!el) {
    return "";
  }
  const ts = els(el, "t");
  if (ts.length === 0) {
    return el.textContent || "";
  }
  let s = "";
  for (let i = 0; i < ts.length; i++) {
    s += ts[i].textContent || "";
  }
  return s;
};

/** Liest die erste Tabelle einer `.xlsx` als Zeilen-Matrix (Strings). */
export const xlsxToMatrix = (bytes: Uint8Array): string[][] => {
  const files = unzipSync(bytes);
  const parser = new DOMParser();

  // sharedStrings (optional; echtes Excel nutzt sie).
  const shared: string[] = [];
  if (files["xl/sharedStrings.xml"]) {
    const doc = parser.parseFromString(
      dec(files["xl/sharedStrings.xml"]),
      "application/xml",
    );
    const sis = els(doc, "si");
    for (let i = 0; i < sis.length; i++) {
      shared.push(textOf(sis[i]));
    }
  }

  // Erste Worksheet-Datei (sheet1 bevorzugt, sonst die erste alphabetisch).
  const sheetPath =
    "xl/worksheets/sheet1.xml" in files
      ? "xl/worksheets/sheet1.xml"
      : Object.keys(files)
          .filter((p) => /^xl\/worksheets\/.*\.xml$/.test(p))
          .sort()[0];
  if (!sheetPath) {
    return [];
  }
  const doc = parser.parseFromString(dec(files[sheetPath]), "application/xml");

  const matrix: string[][] = [];
  const rows = els(doc, "row");
  for (let i = 0; i < rows.length; i++) {
    const cells = els(rows[i], "c");
    const out: string[] = [];
    for (let j = 0; j < cells.length; j++) {
      const c = cells[j];
      const ref = c.getAttribute("r") || "";
      const idx = ref ? colIndexOfRef(ref) : j;
      const t = c.getAttribute("t");
      let value = "";
      if (t === "inlineStr") {
        value = textOf(els(c, "is")[0] || null);
      } else if (t === "s") {
        const v = els(c, "v")[0];
        const n = v ? Number(v.textContent) : NaN;
        value = Number.isInteger(n) ? shared[n] ?? "" : "";
      } else {
        // t="str" (Formel-Ergebnis) und Zahlen/Datum: der <v>-Text.
        const v = els(c, "v")[0];
        value = v ? v.textContent || "" : "";
      }
      // Lücken auffüllen, damit die Spalten spaltengenau ausgerichtet bleiben.
      while (out.length < idx) {
        out.push("");
      }
      out[idx] = value;
    }
    matrix.push(out);
  }
  return matrix;
};
